import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useT } from '../../../i18n';
import type { ApiError } from '../../../lib/errors';
import { vaiTroNenTangRepository } from '../../../lib/repositories/vai-tro-nen-tang.repository';
import { useToast } from '../../../lib/toast';
import { MOT_TRANG } from '../constants';
import type { QuyenNenTang, TaoVaiTroNenTang, VaiTroNenTang } from '../types';

/**
 * Khoá cache.
 *
 * Nhánh riêng `vai-tro-nen-tang`, **không** dưới `['roles']`: nhánh đó là vai
 * trò lớp 2 của tenant (ADR-0041), vô hiệu hoá nó là tải lại màn của tổ chức
 * khách. Hai hệ vai trò không chạm nhau ở đâu cả.
 */
export const KHOA_VAI_TRO_NEN_TANG = {
  danhSach: ['platform-roles', 'danh-sach'] as const,
  danhMucQuyen: ['platform-permissions', 'danh-muc'] as const,
  cuaNhanSu: (userId: string) => ['vai-tro-nen-tang-cua-nhan-su', userId] as const,
  moiNhanSu: ['vai-tro-nen-tang-cua-nhan-su'] as const,
  nguCanh: ['platform-context'] as const,
};

/** 4xx là câu trả lời của server, không phải trục trặc: thử lại chỉ bắt người dùng chờ thêm. */
export function thuLai(soLanDaThu: number, err: unknown): boolean {
  const status = (err as ApiError | undefined)?.status;
  if (status !== undefined && status >= 400 && status < 500) return false;
  return soLanDaThu < 1;
}

/** Trần số trang khi nối: chặn vòng lặp nếu server trả `next_cursor` lặp lại. */
const TRANG_TOI_DA = 50;

/** Cỡ trang lớn nhất API danh mục quyền nhận (ADR-0074 mục 3). */
const TRANG_DANH_MUC = 100;

/**
 * Toàn bộ vai trò nền tảng. Nối `next_cursor` tới hết rồi tìm ở client: nền
 * tảng có vài chục vai trò, và API chưa có tham số tìm theo tên — cùng cách màn
 * vai trò của tenant làm.
 */
export function useDanhSachVaiTroNenTang() {
  return useQuery<VaiTroNenTang[], ApiError>({
    queryKey: KHOA_VAI_TRO_NEN_TANG.danhSach,
    queryFn: async () => {
      const ra: VaiTroNenTang[] = [];
      let cursor: string | undefined;
      for (let i = 0; i < TRANG_TOI_DA; i += 1) {
        const trang = await vaiTroNenTangRepository.danhSach({ limit: MOT_TRANG, cursor });
        ra.push(...trang.items);
        cursor = trang.next_cursor ?? undefined;
        if (!cursor) break;
      }
      return ra;
    },
    staleTime: 30_000,
    retry: thuLai,
  });
}

/**
 * Danh mục quyền nền tảng. Khai trong mã nguồn của backend (ADR-0074 mục 2)
 * nên chỉ đổi khi nền tảng lên phiên bản mới — giữ lâu.
 */
export function useDanhMucQuyenNenTang(bat = true) {
  return useQuery<QuyenNenTang[], ApiError>({
    queryKey: KHOA_VAI_TRO_NEN_TANG.danhMucQuyen,
    queryFn: async () => {
      const ra: QuyenNenTang[] = [];
      let cursor: string | undefined;
      for (let i = 0; i < TRANG_TOI_DA; i += 1) {
        const trang = await vaiTroNenTangRepository.danhMucQuyen({
          limit: TRANG_DANH_MUC,
          cursor,
        });
        ra.push(...trang.items);
        cursor = trang.next_cursor ?? undefined;
        if (!cursor) break;
      }
      return ra;
    },
    staleTime: 5 * 60_000,
    retry: thuLai,
    enabled: bat,
  });
}

/** Ghi vai trò mới trả về vào danh sách trong cache — dòng đổi tại chỗ, không tải lại. */
function useGhiVaiTro() {
  const queryClient = useQueryClient();
  return (moi: VaiTroNenTang) =>
    queryClient.setQueryData<VaiTroNenTang[]>(KHOA_VAI_TRO_NEN_TANG.danhSach, (cu) =>
      cu?.map((r) => (r.id === moi.id ? moi : r)),
    );
}

/**
 * `404` (vai trò vừa bị xoá ở phiên khác) và `422` (mã quyền backend vừa bỏ):
 * tải lại danh sách và danh mục để màn nói đúng hiện trạng.
 */
function useKhiLechDuLieu() {
  const queryClient = useQueryClient();
  return (err: ApiError) => {
    if (err.status === 404) {
      void queryClient.invalidateQueries({ queryKey: KHOA_VAI_TRO_NEN_TANG.danhSach });
    }
    if (err.status === 422) {
      void queryClient.invalidateQueries({ queryKey: KHOA_VAI_TRO_NEN_TANG.danhMucQuyen });
    }
  };
}

/**
 * Tạo vai trò. `Idempotency-Key` mới cho **mỗi lần bấm** (hợp đồng ADR-0074):
 * lần bấm lại sau lỗi mạng mà lần trước đã tới server thì nhận `409` trùng mã,
 * không thành hai vai trò.
 */
export function useTaoVaiTroNenTang() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useT();
  return useMutation<VaiTroNenTang, ApiError, TaoVaiTroNenTang>({
    mutationFn: (du) => vaiTroNenTangRepository.tao(du, crypto.randomUUID()),
    onSuccess: (moi) => {
      void queryClient.invalidateQueries({ queryKey: KHOA_VAI_TRO_NEN_TANG.danhSach });
      toast.success(t('vaiTroNenTang.daTao', { ten: moi.name }));
    },
  });
}

/** Đổi tên. Lỗi không bật toast: form hiện lỗi ngay trong hộp. */
export function useDoiTenVaiTroNenTang() {
  const toast = useToast();
  const { t } = useT();
  const ghi = useGhiVaiTro();
  const khiLech = useKhiLechDuLieu();
  return useMutation<VaiTroNenTang, ApiError, { id: string; name: string }>({
    mutationFn: ({ id, name }) => vaiTroNenTangRepository.doiTen(id, name),
    onSuccess: (moi) => {
      ghi(moi);
      toast.success(t('vaiTroNenTang.daDoiTen', { ten: moi.name }));
    },
    onError: khiLech,
  });
}

/**
 * Thay cả tập quyền của một vai trò.
 *
 * Quyền vừa đổi có hiệu lực ở `platform-context` của **mọi người giữ vai trò
 * đó**, kể cả người đang bấm, nên vô hiệu hoá ngữ cảnh để thanh điều hướng của
 * chính họ cập nhật ngay thay vì chờ `staleTime`.
 */
export function useGanQuyenNenTang() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useT();
  const ghi = useGhiVaiTro();
  const khiLech = useKhiLechDuLieu();
  return useMutation<VaiTroNenTang, ApiError, { id: string; permissions: string[] }>({
    mutationFn: ({ id, permissions }) => vaiTroNenTangRepository.ganQuyen(id, permissions),
    onSuccess: (moi) => {
      ghi(moi);
      void queryClient.invalidateQueries({ queryKey: KHOA_VAI_TRO_NEN_TANG.nguCanh });
      toast.success(t('vaiTroNenTang.daLuuQuyen', { ten: moi.name }));
    },
    onError: khiLech,
  });
}

/**
 * Xoá vai trò.
 *
 * Server chặn khi còn người giữ (ADR-0074 mục 7), nên không phải dọn cache vai
 * trò của từng người như màn tenant: xoá thành công nghĩa là chưa ai giữ nó.
 */
export function useXoaVaiTroNenTang() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useT();
  const khiLech = useKhiLechDuLieu();
  return useMutation<void, ApiError, VaiTroNenTang>({
    mutationFn: (r) => vaiTroNenTangRepository.xoa(r.id),
    onSuccess: (_kq, r) => {
      queryClient.setQueryData<VaiTroNenTang[]>(KHOA_VAI_TRO_NEN_TANG.danhSach, (cu) =>
        cu?.filter((x) => x.id !== r.id),
      );
      void queryClient.invalidateQueries({ queryKey: KHOA_VAI_TRO_NEN_TANG.danhSach });
      toast.success(t('vaiTroNenTang.daXoa', { ten: r.name }));
    },
    onError: khiLech,
  });
}
